// Customer labor rates (migration 0054) + labor COST (migration 0072) —
// owner/admin editable. Per product line we show COST then SELL (charge), matching
// the Inventory Cost/Sell Price column order:
//   - Sell (charge): the per-unit customer charge folded into a quote's total
//     (gutter $/LF, insulation $/SF). A job snapshots the effective rate at send.
//   - Cost: what that labor costs the company (same units) — drives margin
//     reporting only; NEVER affects the customer total. Also snapshotted at send.
// Entered in dollars; stored as integer cents per unit. Blank/0 = none.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { getMe, getTenantSettings, patchTenantSettings, type TenantSettingsUpdateInput } from '../api/client.js';
import { Card, CardContent } from './ui/card.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';

/** cents → dollar string for the input (e.g. 450 → "4.50"); 0 → ""). */
function centsToDollars(cents: number): string {
  return cents === 0 ? '' : (cents / 100).toFixed(2);
}

/** dollar string → integer cents (e.g. "4.50" → 450); blank/invalid → 0. */
function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const num = Number(trimmed);
  if (Number.isNaN(num) || num < 0) return null;
  return Math.round(num * 100);
}

export function LaborRatesSection() {
  const queryClient = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });
  const canEdit = me?.role === 'owner' || me?.role === 'admin';
  const { data: settings, isLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: getTenantSettings,
  });

  // [cost, sell] per product line, as dollar strings.
  const [gutterCost, setGutterCost] = useState('');
  const [gutter, setGutter] = useState('');
  const [insulationCost, setInsulationCost] = useState('');
  const [insulation, setInsulation] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setGutterCost(centsToDollars(settings.gutterLaborCostCentsPerLf));
    setGutter(centsToDollars(settings.gutterLaborRateCentsPerLf));
    setInsulationCost(centsToDollars(settings.insulationLaborCostCentsPerSf));
    setInsulation(centsToDollars(settings.insulationLaborRateCentsPerSf));
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (input: TenantSettingsUpdateInput) => patchTenantSettings(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  // Save one field: no-op on blank/invalid or unchanged.
  const saveField = (value: string, current: number, key: keyof TenantSettingsUpdateInput) => {
    if (!canEdit || !settings) return;
    const cents = dollarsToCents(value);
    if (cents === null || cents === current) return;
    mutation.mutate({ [key]: cents });
  };

  const rateInput = (
    id: string,
    value: string,
    setValue: (v: string) => void,
    onSave: () => void,
  ) => (
    <Input
      id={id}
      type="number"
      min="0"
      step="0.01"
      inputMode="decimal"
      placeholder="0.00"
      value={value}
      disabled={!canEdit || mutation.isPending}
      onChange={(e) => {
        setValue(e.target.value);
        setSaved(false);
      }}
      onBlur={onSave}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSave();
      }}
      className="w-32 font-mono"
    />
  );

  return (
    <Card>
      <CardContent className="p-6">
        {isLoading ? (
          <div className="flex gap-3">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-10 w-64" />
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-x-10 gap-y-6">
            {/* Gutter — Cost then Sell (per LF) */}
            <div>
              <div className="eyebrow mb-1.5">Gutter labor ($ / linear foot)</div>
              <div className="flex items-end gap-3">
                <div>
                  <label htmlFor="gutter-labor-cost" className="mb-1 block text-xs text-taupe">
                    Cost
                  </label>
                  {rateInput('gutter-labor-cost', gutterCost, setGutterCost, () =>
                    saveField(gutterCost, settings!.gutterLaborCostCentsPerLf, 'gutterLaborCostCentsPerLf'),
                  )}
                </div>
                <div>
                  <label htmlFor="gutter-labor-rate" className="mb-1 block text-xs text-taupe">
                    Sell
                  </label>
                  {rateInput('gutter-labor-rate', gutter, setGutter, () =>
                    saveField(gutter, settings!.gutterLaborRateCentsPerLf, 'gutterLaborRateCentsPerLf'),
                  )}
                </div>
              </div>
            </div>
            {/* Insulation — Cost then Sell (per SF) */}
            <div>
              <div className="eyebrow mb-1.5">Insulation labor ($ / square foot)</div>
              <div className="flex items-end gap-3">
                <div>
                  <label htmlFor="insulation-labor-cost" className="mb-1 block text-xs text-taupe">
                    Cost
                  </label>
                  {rateInput('insulation-labor-cost', insulationCost, setInsulationCost, () =>
                    saveField(
                      insulationCost,
                      settings!.insulationLaborCostCentsPerSf,
                      'insulationLaborCostCentsPerSf',
                    ),
                  )}
                </div>
                <div>
                  <label htmlFor="insulation-labor-rate" className="mb-1 block text-xs text-taupe">
                    Sell
                  </label>
                  <div className="flex items-center gap-2">
                    {rateInput('insulation-labor-rate', insulation, setInsulation, () =>
                      saveField(
                        insulation,
                        settings!.insulationLaborRateCentsPerSf,
                        'insulationLaborRateCentsPerSf',
                      ),
                    )}
                    {saved && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <p className="mt-3 text-xs text-taupe">
          {canEdit
            ? 'Sell is the customer charge folded into a job’s quote total (gutter footage × $/LF, insulation area × $/SF). Cost is what that labor costs you — it drives margin only and never changes the customer total. Both lock onto a job when its quote is sent; changing them here never reprices already-sent quotes. Blank/0 = none.'
            : 'Only an owner or admin can edit labor rates.'}
        </p>
        {mutation.error && (
          <div className="mt-2 text-xs text-destructive">
            {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
